import * as React from "react";
import type { CollaborateSelectionContext } from "./types/selection";
import type { CollaborateAgreementVersion, InboxSummary } from "./types/collaborateThread";
import { hydrateCollaborateData, refetchCollaborateThreads, resetCollaborateSession } from "./api";
import { useCollaborateThreadPolling } from "./hooks/useCollaborateThreadPolling";
import { useCollaborateStore } from "./store/useCollaborateStore";
import CollaborateFilterRow from "./components/CollaborateFilterRow";
import CollaborateThreadList from "./components/CollaborateThreadList";
import CommentDetail from "./components/CommentDetail";
import Composer from "./components/Composer";
import CollabComposeCard from "./components/ui/CollabComposeCard";
import { CollabToastHost } from "./components/ui/CollabToast";
import { InboxWelcomeToast } from "./components/ui/InboxWelcomeToast";
import "./styles/collaborate.scss";
import "./styles/collaborate-ui.scss";

export type CollaboratePaneProps = {
  active: boolean;
  selectedContext: CollaborateSelectionContext;
  focusComposer?: boolean;
  onFocusComposerHandled?: () => void;
  /** Optional document id passed to fetchThreads when hydrating. */
  documentId?: string;
  /** Called after a comment on document selection is posted (clears taskpane selection UI). */
  onSelectionConsumed?: () => void;
  versions?: CollaborateAgreementVersion[];
  defaultComposerVersionId?: string | null;
  /** Open document version id — used for anchor jump. */
  currentVersionId?: string | null;
  /** Contract-wide inbox counts (unread / mentions / sign-offs). */
  inboxSummary?: InboxSummary | null;
  /** Current auth token — scopes the inbox welcome toast to this login. */
  accessToken?: string | null;
};

const CollaboratePane: React.FC<CollaboratePaneProps> = ({
  active,
  selectedContext,
  focusComposer = false,
  onFocusComposerHandled,
  documentId,
  onSelectionConsumed,
  versions,
  defaultComposerVersionId,
  currentVersionId = null,
  inboxSummary = null,
  accessToken = null,
}) => {
  const activeThreadId = useCollaborateStore((s) => s.activeThreadId);
  const listView = useCollaborateStore((s) => s.listView);
  const composerSnippet = useCollaborateStore((s) => s.composerSnippet);
  const focusHandledRef = React.useRef(false);
  const prevActiveRef = React.useRef(false);
  const prevActiveThreadIdRef = React.useRef<string | null>(null);
  const prevListViewRef = React.useRef(listView);

  useCollaborateThreadPolling(active, documentId);

  React.useEffect(() => {
    if (!active) {
      return undefined;
    }

    const { hydrated } = useCollaborateStore.getState();
    if (hydrated) {
      void refetchCollaborateThreads(documentId);
    } else {
      void hydrateCollaborateData(documentId);
    }

    return () => {
      resetCollaborateSession();
    };
  }, [active, documentId]);

  React.useEffect(() => {
    const wasActive = prevActiveRef.current;
    const prevThreadId = prevActiveThreadIdRef.current;
    const prevView = prevListViewRef.current;

    prevActiveRef.current = active;
    prevActiveThreadIdRef.current = activeThreadId;
    prevListViewRef.current = listView;

    if (!active || activeThreadId || listView !== "comments" || focusComposer) {
      return undefined;
    }

    const tabJustActivated = active && !wasActive;
    const returnedToList = active && prevThreadId != null && activeThreadId == null;
    const switchedToComments = active && prevView !== "comments" && listView === "comments";

    if (!tabJustActivated && !returnedToList && !switchedToComments) {
      return undefined;
    }

    const quote = tabJustActivated ? (selectedContext?.quote?.trim() ?? "") : "";
    const t = window.setTimeout(() => {
      const { openComposer } = useCollaborateStore.getState();
      openComposer(quote ? { quote } : null);
    }, 50);
    return () => window.clearTimeout(t);
  }, [active, activeThreadId, listView, focusComposer, selectedContext?.quote]);

  React.useEffect(() => {
    if (!focusComposer) {
      focusHandledRef.current = false;
    }
  }, [focusComposer]);

  React.useEffect(() => {
    if (!active || !focusComposer || focusHandledRef.current) {
      return undefined;
    }
    focusHandledRef.current = true;
    const quote = selectedContext?.quote ?? "";
    const t = window.setTimeout(() => {
      const { openComposer } = useCollaborateStore.getState();
      if (quote) {
        openComposer({ quote });
      } else {
        openComposer(null);
      }
      onFocusComposerHandled?.();
    }, 50);
    return () => window.clearTimeout(t);
  }, [active, focusComposer, onFocusComposerHandled, selectedContext?.quote]);

  if (!active) {
    return null;
  }

  const showTopComposer = !activeThreadId && listView === "comments";

  return (
    <div className="collaborate-pane">
      <CollabToastHost />
      <InboxWelcomeToast
        summary={inboxSummary}
        active={active}
        accessToken={accessToken}
        documentId={documentId}
      />
      {!activeThreadId ? (
        <>
          {showTopComposer ? (
            <div className="collab-compose-top">
              <CollabComposeCard>
                <Composer
                  snippet={composerSnippet}
                  documentSelection={selectedContext}
                  onSelectionConsumed={onSelectionConsumed}
                  versions={versions}
                  defaultVersionId={defaultComposerVersionId}
                />
              </CollabComposeCard>
            </div>
          ) : null}
          <CollaborateFilterRow documentId={documentId} />
        </>
      ) : null}
      <div className="collaborate-pane__body">
        {activeThreadId ? (
          <CommentDetail
            threadId={activeThreadId}
            versions={versions}
            defaultComposerVersionId={defaultComposerVersionId}
            currentVersionId={currentVersionId}
          />
        ) : (
          <CollaborateThreadList
            selectedContext={selectedContext}
            onSelectionConsumed={onSelectionConsumed}
            documentId={documentId}
            versions={versions}
            defaultComposerVersionId={defaultComposerVersionId}
            currentVersionId={currentVersionId}
          />
        )}
      </div>
    </div>
  );
};

export default CollaboratePane;
